/**
 * Global provider for public platform settings (support phone, WhatsApp,
 * email, etc.). Fetched once at boot from `/api/public/site-config`; all
 * components that need to render a WhatsApp / call / email link should
 * read the value from here instead of hard-coding a number, so a Super
 * Admin can change it from Platform Settings → General without a redeploy.
 *
 * Fallbacks in `DEFAULTS` mirror `platform_defaults.py` so the UI never
 * renders an empty link when the API is unreachable at first paint.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  whatsapp_number: "917015989991",
  support_mobile: "+91 70159-89991",
  support_email: "info@stewardindia.com",
  platform_name: "Steward Learning Cloud",
  academy_name: "Steward Institute of Healthcare Management & Technology",
  company_name: "Steward Healthcare India Pvt. Ltd.",
};

const Ctx = createContext({ config: DEFAULTS, loading: true, refresh: () => {} });

export function SiteConfigProvider({ children }) {
  const [state, setState] = useState({ config: DEFAULTS, loading: true });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/public/site-config");
      setState({ config: { ...DEFAULTS, ...(data.data?.config || {}) }, loading: false });
    } catch {
      setState({ config: DEFAULTS, loading: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Public settings hook. */
export function useSiteConfig() {
  return useContext(Ctx);
}

/** WhatsApp number without ``+`` — safe for use inside a `wa.me/` URL. */
export function useWhatsAppNumber() {
  const { config } = useContext(Ctx);
  return (config.whatsapp_number || DEFAULTS.whatsapp_number).replace(/\D/g, "");
}

/** Build a wa.me URL that opens WhatsApp with a pre-filled message. */
export function useWhatsAppUrl(message = "") {
  const num = useWhatsAppNumber();
  return message
    ? `https://wa.me/${num}?text=${encodeURIComponent(message)}`
    : `https://wa.me/${num}`;
}

/** Formatted display + tel: URL for the support-call number. */
export function useSupportPhone() {
  const { config } = useContext(Ctx);
  const display = config.support_mobile || DEFAULTS.support_mobile;
  const dial = "+" + (config.whatsapp_number || DEFAULTS.whatsapp_number).replace(/\D/g, "");
  return { display, telHref: `tel:${dial}` };
}
